import { CommunityFeature, UploadContextType, type VariantType } from "@upsquad/shared";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { PageHelmet } from "@/src/common/components/page-helmet";
import { PhotoUpload } from "@/src/common/components/photo-upload";
import { Button, Spinner } from "@/src/common/components/ui";
import { useAppDispatch, useAppSelector } from "@/src/common/redux/store";
import { useCreateCommunityMutation } from "@/src/community/services/community-http.service";
import { getCommunities } from "@/src/community/store/community-selectors";
import { setCommunities } from "@/src/community/store/community-slice";
import { CommunityPreviewCard } from "@/src/onboarding/components/community-preview-card";
import { FeatureToggles } from "@/src/onboarding/components/feature-toggles";
import { CongratulationsModal } from "@/src/onboarding/components/modals/congratulations-modal";
import { OnboardingLayout } from "@/src/onboarding/components/onboarding-layout";
import { ThemeSelector } from "@/src/onboarding/components/theme-selector";
import { ONBOARDING_ROUTES } from "@/src/onboarding/constants/onboarding-routes";
import { getCreateCommunityDraft } from "@/src/onboarding/store/onboarding-selectors";
import { clearDraft } from "@/src/onboarding/store/onboarding-slice";

const ALL_FEATURES = Object.values(CommunityFeature);

export default function CreateCommunityPersonalizePage() {
	const { t } = useTranslation("onboarding");
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const draft = useAppSelector(getCreateCommunityDraft);
	const existingCommunities = useAppSelector(getCommunities);
	const [createCommunity, { isLoading }] = useCreateCommunityMutation();

	const [imageUrl, setImageUrl] = useState<string | null>(null);
	const [variant, setVariant] = useState<VariantType>("default");
	const [features, setFeatures] = useState<CommunityFeature[]>([...ALL_FEATURES]);
	const [showCongrats, setShowCongrats] = useState(false);
	const [createdCommunityName, setCreatedCommunityName] = useState("");
	const [createdCommunitySlug, setCreatedCommunitySlug] = useState("");

	useEffect(() => {
		if (!draft && !showCongrats) navigate(ONBOARDING_ROUTES.CREATE_COMMUNITY_PROFILE);
	}, [draft, showCongrats, navigate]);

	useEffect(() => {
		const prevVariant = document.documentElement.getAttribute("data-variant");
		document.documentElement.setAttribute("data-variant", variant);
		return () => {
			if (prevVariant) document.documentElement.setAttribute("data-variant", prevVariant);
			else document.documentElement.removeAttribute("data-variant");
		};
	}, [variant]);

	if (!draft && !showCongrats) return null;

	const handleSubmit = async () => {
		if (!draft) return;

		const payload = {
			name: draft.name,
			slug: draft.slug,
			description: draft.description,
			image: imageUrl ?? undefined,
			variant,
			features,
		};

		const result = await createCommunity(payload).unwrap();
		if (result?.community) {
			const community = result.community;
			setCreatedCommunityName(community.name);
			setCreatedCommunitySlug(community.slug);
			dispatch(setCommunities([...existingCommunities, community]));
			dispatch(clearDraft());
			setShowCongrats(true);
		}
	};

	return (
		<>
			<PageHelmet title="Personalize Community" />
			{!showCongrats && draft && (
				<OnboardingLayout leftContent={<CommunityPreviewCard name={draft.name} slug={draft.slug} imageUrl={imageUrl} />} step={3} showBack>
					<h1 className="text-2xl font-bold mb-2">{t("personalize_title")}</h1>
					<p className="text-sm text-foreground/50 mb-8">{t("personalize_subtitle")}</p>

					<div className="flex flex-col gap-6">
						<PhotoUpload value={imageUrl} onChange={setImageUrl} context={{ type: UploadContextType.USER_AVATAR }} variant="logo" />

						<ThemeSelector value={variant} onChange={setVariant} />

						<FeatureToggles value={features} onChange={setFeatures} />

						<Button variant="primary" fullWidth isPending={isLoading} onPress={handleSubmit}>
							{({ isPending }) => (
								<>
									{isPending ? <Spinner color="current" size="sm" /> : null}
									{t("create_space")}
								</>
							)}
						</Button>
					</div>
				</OnboardingLayout>
			)}

			<CongratulationsModal isOpen={showCongrats} communityName={createdCommunityName} communitySlug={createdCommunitySlug} />
		</>
	);
}
